import React, { type HTMLAttributes } from 'react';

import { Link } from '@/components/ui/link';
import { cls } from '@/utils/cls';

import { TouchTarget } from '../button/button-catalyst';

export function Navbar({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'nav'>) {
  return (
    <nav
      {...props}
      className={cls('flex flex-1 items-center gap-4 py-2.5', className)}
    />
  );
}

export function NavbarDivider({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={cls('h-6 w-px bg-zinc-950/10 dark:bg-white/10', className)}
    />
  );
}

export function NavbarSection({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div {...props} className={cls('flex items-center gap-3', className)} />
  );
}

export function NavbarSpacer({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={cls('-ml-4 flex-1', className)}
    />
  );
}

export const NavbarItem = React.forwardRef(function NavbarItem(
  {
    current,
    className,
    children,
    ...props
  }: { current?: boolean; className?: string; children: React.ReactNode } & (
    | Omit<HTMLAttributes<HTMLButtonElement>, 'className'>
    | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
  const classes = cls(
    // Base
    'relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left text-base/6 font-base text-zinc-950',
    // Leading icon/icon-only
    'data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 data-[slot=icon]:*:fill-zinc-500 sm:data-[slot=icon]:*:size-5',
    // Trailing icon (down chevron or similar)
    'data-[slot=icon]:last:[&:not(:nth-child(2))]:*:ml-auto data-[slot=icon]:last:[&:not(:nth-child(2))]:*:size-5 sm:data-[slot=icon]:last:[&:not(:nth-child(2))]:*:size-4',
    // Avatar
    'data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--avatar-radius:theme(borderRadius.DEFAULT)] data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6',
    // Hover
    'hover:bg-zinc-950/5 data-[slot=icon]:*:hover:fill-zinc-950',
    // Active
    'active::bg-zinc-950/5 data-[slot=icon]:*:active::fill-zinc-950',
    // Dark mode
    'dark:text-white dark:data-[slot=icon]:*:fill-zinc-400',
    'dark:hover:bg-white/5 dark:data-[slot=icon]:*:hover:fill-white',
    'dark:active::bg-white/5 dark:data-[slot=icon]:*:active::fill-white',
  );

  return (
    <span className={cls('relative', className)}>
      {current && (
        <span className="absolute inset-x-2 -bottom-2.5 h-0.5 scale-x-100 rounded-full bg-zinc-950 transition-all duration-300 dark:bg-white" />
      )}
      {'to' in props ? (
        <Link
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <button
          {...props}
          className={cls('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref as React.RefObject<HTMLButtonElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </button>
      )}
    </span>
  );
});

export function NavbarLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={cls('truncate', className)} />;
}
